// ProductListView.tsx
import React, { useState, useEffect, useRef } from "react";
import { RefreshCw, Upload, Loader2, ArrowRight, X, ImageIcon, FileTextIcon, TagIcon, XIcon  } from "lucide-react";
import { useSyncShopify } from "../../hooks/useSyncShopify";
import { Card } from "../../components/ui/card";
import { ProductQualityMetric } from "../../types/api";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import ProductRow from "./ProductRow";
import ProductsFilters from "./ProductsFilters";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import { RootState } from "../../store/store";
import { setCurrentPage, setSearchKeyword, setQualityGroup } from "../../store/filtersSlice";
import { useSelector, useDispatch } from "react-redux";
import { ShopifyImportStats } from "../../admin/dashboards/types";
import { setEnrichmentDialogOpen, setReimportExcelDialogOpen } from "../../store/flowSlice";
import { ContentQualityOverview } from "../../types/api";
import ContentQualityActionCard from "./ContentSuggestionCard";
import { toast } from "../../components/hooks/use-toast";
import { deleteGenericJob } from "../../store/jobsSlice";
import { useTranslation } from "../../i18n/hooks";

type ProductListViewProps = {
  products: ProductQualityMetric[];
  summary: ContentQualityOverview | null | undefined;
  isFetching: boolean;
  isRefetching: boolean;
  error: string | null;
};

const ProductListView = ({
  products,
  isFetching,
  error,
  summary,
  isRefetching
}: ProductListViewProps) => {
  const { t } = useTranslation();
  const { currentPage } = useSelector((state: RootState) => state.filters);
  const { currentCollection, currentUser } = useSelector((state: RootState) => state.user);
  const {activeGenericJobs} = useSelector((state: RootState) => state.jobs);
  const reimportExcelJob = activeGenericJobs["reimport_excel"];

  useEffect(() => {
    if (!reimportExcelJob) return;

    const { status } = reimportExcelJob;
    
    if (status === "running") {
        setIsReimportingExcel(true);
        setExcelImportProgress(reimportExcelJob.progress);
    } else if (status === "completed" || status === "failed") {
        setIsReimportingExcel(false);
        
        toast({
            title: status === "completed" ? t('productsView.excel_import_completed') : t('productsView.excel_import_failed'),
            description: status === "completed" 
                ? t('productsView.excel_import_success_msg')
                : t('productsView.excel_import_failure_msg'),
            variant: status === "completed" ? "default" : "destructive"
        });

        const timeoutId = setTimeout(() => {
            dispatch(deleteGenericJob("reimport_excel"));
            if(status === "completed"){
              window.location.reload();
            }
        }, 1000);

        return () => clearTimeout(timeoutId);
    }
}, [reimportExcelJob]);

  const [stats, setStats] = useState<ShopifyImportStats | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!currentCollection.collection?.stats) return;
    const stats = JSON.parse(currentCollection?.collection.stats!) as ShopifyImportStats;
    setStats(stats);
  }, [currentCollection.collection]);

  const { syncShopify, isSyncing, progress, lastSync } = useSyncShopify({
    onCompleted: () => {
      window.location.reload();
      toast({
        title: t('productsView.sync_completed'),
        variant: "default"
      });
    }
  });
  const { lastUnsyncedJob } = useSelector((state: RootState) => state.flow);
  const itemsPerPage = 10;
  const dispatch = useDispatch();
  const totalPages = Math.ceil((summary?.filtered_product_count ?? 0) / itemsPerPage);
  const currentProducts = products;


  const handleSelectProduct = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === currentProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(currentProducts.map(p => p.product_id)));
    }
  };

  const [dismissedWarning, setDismissedWarning] = useState(false);

  const handleClearSelection = () => {
    setSelectedProducts(new Set());
  };

  const getEstimatedImpact = (score: number | null) => {
    if (!score || score >= 90) return null;

    if (score < 30) {
      return t('qualityMessages.impact_high');
    } else if (score >= 30 && score < 70) {
      return t('qualityMessages.impact_medium');
    } else if (score >= 70 && score < 90) {
      return t('qualityMessages.impact_low');
    }

    return null;
  };

  const [excelImportProgress, setExcelImportProgress] = useState(0);
  const [isReimportingExcel, setIsReimportingExcel] = useState(false);

  const estimateScoreImprovement = () => {
    if (!stats?.total_products) return 0;
    const baseScore = 22;
    const maxScore = 100;
    const missingDescriptions = stats.total_products - stats.products_with_description;
    const score = baseScore + (missingDescriptions / stats.total_products) * (maxScore - baseScore);
    return Math.round(score);
  };

  const handleEnrichSelected = () => {
    dispatch(setEnrichmentDialogOpen({
      isOpen: true,
      enrichedProducts: Array.from(selectedProducts),
      selectedFeatures: ["title", "description", "seo", "altText", "productTags"]

    }));
  };

  const estimateScore = () => {
    if (!stats?.total_products) return 0;
    const baseScore = 22;
    const maxScore = 100;
    const missingDescriptions =
      stats.total_products - stats.products_with_description;
    const score =
      baseScore +
      (missingDescriptions / stats.total_products) * (maxScore - baseScore);
    return Math.round(score);
  };

 // Fixed version of the problematic sections
const StatusCard = () => {
  // Add any missing imports at the top of your file
  const qualityGroup = useSelector((state: RootState) => state.filters.qualityGroup);
  const searchKeyword = useSelector((state: RootState) => state.filters.searchKeyword);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isRecentExport = lastUnsyncedJob && 
  (new Date().getTime() - new Date(lastUnsyncedJob.createdAt).getTime()) < 24 * 60 * 60 * 1000;

  return (
    <Card className="bg-white shadow-sm">     
      <div className="p-6">
      {isRecentExport && !dismissedWarning && (
  <Alert className="mb-6 bg-blue-50 border-blue-200">
    <AlertDescription className="flex items-center justify-between text-blue-800">
      <span>{t('productsView.export_enrichment_message')}</span>
      <div className="flex gap-2">
        <Button 
          variant="ghost" 
          size="sm"
          className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
          onClick={() => navigate("/admin/jobs")}
        >
          {t('productsView.view_and_export')}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setDismissedWarning(true)}
          className="h-8 w-8 p-0 text-blue-700 hover:text-blue-900 hover:bg-blue-100"
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </div>
    </AlertDescription>
  </Alert>
)}

          <div>
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-lg md:text-2xl font-bold tracking-tight">
                  {t('productsView.content_transformation_title')}
                </h3>
                <p className="text-md md:text-lg">
                  {t('productsView.content_transformation_description')}
                </p>
                
 
              </div>

              <div className="mt-4 w-full md:w-fit md:mt-0 flex gap-3">
                <Button 
                  size="lg"
                  className="bg-blue-600 w-full hover:bg-blue-700 text-white shadow-sm "
                  onClick={() => dispatch(setEnrichmentDialogOpen({ 
                    isOpen: true,
                    selectedFeatures: ["title", "description", "seo", "altText", "productTags"]
                  }))}                >
                  {t('dashboard.fix_all_issues')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
      </div>

      {/* Issue Categories */}
      <div className="border-t">
      <div className="grid grid-cols-1 md:grid-cols-3 divide-x divide-y md:divide-y-0">
      {((summary?.missing_images ?? 0) > 0) && (
            <button
              onClick={() => dispatch(setQualityGroup(
                qualityGroup === 'missing_images' ? 'all_products' : 'missing_images'
              ))}
              className={`p-6 text-left transition-colors ${
                qualityGroup === 'missing_images' ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <ImageIcon className="h-4 w-4 text-gray-500" />
                <span className="font-medium">{t('productsView.missing_images')}</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-semibold">{summary?.missing_images || 0}</span>
                <span className="text-sm text-gray-500">{summary?.missing_images === 1 ? t('productsView.product_singular') : t('productsView.product_plural')}</span>
              </div>
            </button>
          )}

{((summary?.missing_description ?? 0) > 0) && (
            <button
              onClick={() => dispatch(setQualityGroup(
                qualityGroup === 'missing_description' ? 'all_products' : 'missing_description'
              ))}
              className={`p-4 text-left transition-colors ${
                qualityGroup === 'missing_description' ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <FileTextIcon className="h-4 w-4 text-gray-500" />
                <span className="font-medium">{t('productsView.poor_descriptions')}</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-semibold">{summary?.missing_description || 0}</span>
                <span className="text-sm text-gray-500">{t('productsView.product_plural')}</span>
              </div>
            </button>
          )}

{((summary?.missing_tags ?? 0) > 0) && (
            <button
              onClick={() => dispatch(setQualityGroup(
                qualityGroup === 'missing_tags' ? 'all_products' : 'missing_tags'
              ))}
              className={`p-4 text-left transition-colors ${
                qualityGroup === 'missing_tags' ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <TagIcon className="h-4 w-4 text-gray-500" />
                <span className="font-medium">{t('productsView.missing_tags')}</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-semibold">{summary?.missing_tags || 0}</span>
                <span className="text-sm text-gray-500">{t('productsView.product_plural')}</span>
              </div>
            </button>
          )}
        </div>
      </div>

    </Card>
);
};
  return (
    <div className="p-2 md:p-6 flex flex-col gap-6">
      {/* Replace the hero card and quality overview with StatusCard */}
      <StatusCard />

      {/* Filters and Search */}
      {!error && <ProductsFilters />}

      {/* Products List */}
      {!error && !isFetching && !isRefetching && products && (
        <div className="bg-white rounded-lg shadow-sm border">
          {/* Sync Message Strip */}
          {currentCollection?.collection?.miqImportMethod === "shopify" && 
          <div className="px-4 py-2 bg-blue-50 border-b flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <RefreshCw className="h-4 w-4" />
              {t('productsView.last_synced')} {lastSync || t('productsView.never')}
              {(lastSync?.includes("hours ago") && parseInt(lastSync) >= 2) ||
              lastSync?.includes("days") ||
              lastSync?.includes("months") ||
              !lastSync
                ? t('productsView.consider_refreshing')
                : ""}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => syncShopify()}
              disabled={isSyncing}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('productsView.syncing')} ({progress}%)
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('productsView.refresh_now')}
                </>
              )}
            </Button>
          </div>}
          {currentCollection?.collection?.miqImportMethod === "excel" && (
            <div className="px-4 py-2 bg-blue-50 border-b flex items-center justify-end">
             <Button
      variant="outline"
      size="default"
             onClick={() => {
              dispatch(setReimportExcelDialogOpen(true));
             }}
             disabled={isReimportingExcel}
             className="text-blue-600 hover:text-blue-700 hover:bg-blue-100 border-blue-300 hover:border-blue-400 font-medium"
             >
             {isReimportingExcel ? (
               <>
                 <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                 {t('productsView.importing_excel')} ({excelImportProgress}%)
               </>
             ) : (
               <>
                 <Upload className="h-4 w-4 mr-2" />
                 {t('productsView.import_your_excel')}
               </>
             )}
           </Button>
           </div>
          )}
          
          {/* Selection Action Bar */}
          {selectedProducts.size > 0 && (
            <div className="sticky top-0 z-10 bg-blue-50 border-b p-0 md:p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <X className="w-4 h-4 mr-2" />
                  <span className="hidden md:inline-block">
                    {t('productsView.clear_selection')}
                  </span>
                </Button>
                <span className="text-sm text-gray-600">
                  {selectedProducts.size}{" "}
                  {selectedProducts.size === 1 ? t('productsView.product_singular') : t('productsView.product_plural')}{" "}
                  {t('productsView.selected')}
                </span>
              </div>
              <div className="flex gap-2 p-2 md:p-0">
                <Button
                  className=" bg-blue-600 hover:bg-blue-700 ml-auto flex items-center gap-2"
                  variant="default"
                  onClick={handleEnrichSelected}
                >
                  {t('productsView.enhance_selected')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Header Row */}
          <div
            ref={headerRef}
            className="px-3 md:px-6 py-4 border-b flex gap-2 md:gap-4 items-center text-sm font-medium text-gray-500"
          >
            <Checkbox
              checked={
                selectedProducts.size === currentProducts.length &&
                currentProducts.length > 0
              }
              onCheckedChange={handleSelectAll}
              aria-label={t('productsView.select_all_products')}
            />
            <div className="w-full md:gap-4 grid grid-cols-[2fr,120px] md:grid-cols-[2.5fr,120px,120px,100px,20px] lg:grid-cols-[3fr,140px,140px,120px,20px]">
            <div>{t('productsView.product')}</div>

            <div className="text-left">{t('productsView.content_score')}</div>
            <div className="hidden md:block text-center">{t('productsView.low_score')}</div>
            <div className="hidden md:block text-right pr-4">{t('productsView.action')}</div>
            </div>
          </div>

          {/* Product Rows */}
          {currentProducts.map((product) => (
            <div
              key={product.product_id}
              className={`border-b hover:bg-gray-50/50 transition-colors ${
                selectedProducts.has(product.product_id) ? "bg-blue-50/50" : ""
              }`}
            >
              <div className="pl-2 md:pl-6 py-3 grid grid-cols-[auto,1fr] gap-4 items-center relative">
                {/* Checkbox area - full height clickable */}
                <div
                  className="absolute left-3 md:left-6 top-0 bottom-0 w-[18px] cursor-pointer flex items-center justify-center"
                  onClick={() => handleSelectProduct(product.product_id)}
                >
                  <Checkbox
                    checked={selectedProducts.has(product.product_id)}
                    className="pointer-events-none"
                    aria-label={`${t('productsView.select')} ${product.title}`}
                  />
                </div>

                {/* Product row content */}
                <div className="w-[18px]" />
                <ProductRow
                  product={product}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {t('productsView.error_loading_products')}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {error ||
                  t('productsView.unexpected_error')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {(isFetching || isRefetching) && (
        <div className="flex items-center justify-center h-[16px] bg-blue-50 shadow-md border rounded-md p-12">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      )}

      {/* Pagination */}
      {!error && !isFetching && !isRefetching && products && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious>{t('common.previous')}</PaginationPrevious>
            </PaginationItem>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((pageNum) => {
                // Always show first and last page
                if (pageNum === 1 || pageNum === totalPages) return true;
                // Show 2 pages before and after current page
                return Math.abs(currentPage - pageNum) <= 2;
              })
              .map((pageNum, i, array) => {
                // Add ellipsis if there's a gap
                if (i > 0 && pageNum - array[i - 1] > 1) {
                  return (
                    <React.Fragment key={`${pageNum}-fragment`}>
                      <PaginationItem>
                        <span className="px-3 py-2">...</span>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => dispatch(setCurrentPage(pageNum))}
                          isActive={currentPage === pageNum}
                          className="cursor-pointer hover:bg-white"
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    </React.Fragment>
                  );
                }

                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      onClick={() => dispatch(setCurrentPage(pageNum))}
                      isActive={currentPage === pageNum}
                      className="cursor-pointer hover:bg-white"
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

            <PaginationItem>
              <PaginationNext>{t('common.next')}</PaginationNext>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {/* Smart Assistant Card */}
      <ContentQualityActionCard tableId={currentCollection?.collection?.tableId!} />
    </div>
  );
};

export default ProductListView;